import { Database, KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { AccountSummary } from '@/features/auth/components/account-summary';
import { SignOutConfirm } from '@/features/auth/components/sign-out-confirm';
import { NAVIGATION } from '@/shared/config/navigation';
import { msg } from '@/shared/i18n/msg';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Sidebar } from '@/shared/ui/sidebar/sidebar';
import { SidebarContent } from '@/shared/ui/sidebar/sidebar-content';
import { SidebarFooter } from '@/shared/ui/sidebar/sidebar-footer';
import { SidebarGroup } from '@/shared/ui/sidebar/sidebar-group';
import { SidebarGroupContent } from '@/shared/ui/sidebar/sidebar-group-content';
import { SidebarGroupLabel } from '@/shared/ui/sidebar/sidebar-group-label';
import { SidebarHeader } from '@/shared/ui/sidebar/sidebar-header';
import { SidebarMenu } from '@/shared/ui/sidebar/sidebar-menu';
import { SidebarMenuButton } from '@/shared/ui/sidebar/sidebar-menu-button';
import { SidebarMenuItem } from '@/shared/ui/sidebar/sidebar-menu-item';
import { useSidebar } from '@/shared/ui/sidebar/use-sidebar';
import { LocaleMenu } from './locale-menu';
import { ShellFooter } from './shell-footer';
import { ThemeMenu } from './theme-menu';

const ACCOUNT = [
  { to: '/data', label: msg('Data'), icon: Database },
  { to: '/access', label: msg('Sessions'), icon: KeyRound },
] as const;

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Desktop: collapsible to icons (⌘B). Phones: the same content in a sheet opened by "More",
 * plus the theme, the language and the footer line the desktop shows in the header and footer.
 */
export function AppSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  // Navigating from the phone sheet closes it.
  useEffect(() => {
    if (pathname) setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  const primary = NAVIGATION.filter((item) => item.primary);
  const records = NAVIGATION.filter((item) => !item.primary);

  return (
    <Sidebar collapsible="icon" className="group-data-[side=left]:border-r-0">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" tooltip={t('Survival kit')} render={<Link to="/" />}>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                <ShieldCheck className="size-4.5" />
              </span>
              <span className="truncate font-semibold tracking-tight">{t('Survival kit')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu aria-label={t('Sections')} className="gap-1">
              {primary.map(({ to, label, icon: Icon }) => (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton
                    isActive={isActive(pathname, to)}
                    tooltip={t(label)}
                    render={<Link to={to} />}
                  >
                    <Icon />
                    <span>{t(label)}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>{t('Records')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {records.map(({ to, label, icon: Icon }) => (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton
                    isActive={isActive(pathname, to)}
                    tooltip={t(label)}
                    render={<Link to={to} />}
                  >
                    <Icon />
                    <span>{t(label)}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu className="gap-1">
          {ACCOUNT.map(({ to, label, icon: Icon }) => (
            <SidebarMenuItem key={to}>
              <SidebarMenuButton
                isActive={isActive(pathname, to)}
                tooltip={t(label)}
                render={<Link to={to} />}
              >
                <Icon />
                <span>{t(label)}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
          <SidebarMenuItem>
            <SignOutConfirm
              trigger={
                <SidebarMenuButton tooltip={t('Sign out')}>
                  <LogOut />
                  <span>{t('Sign out')}</span>
                </SidebarMenuButton>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
        <AccountSummary />
        {isMobile && (
          <>
            <div className="flex items-center gap-1">
              <ThemeMenu />
              <LocaleMenu />
            </div>
            <ShellFooter className="h-auto flex-col items-start gap-1 px-0" />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
